import { useContainerWidth } from '@autone/utils';
import { type ReactNode, useCallback } from 'react';

import {
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../atoms';
import { cn } from '../../utils';

import { SortDirectionIndicator } from './sort-indicator';
import { type SortInput } from './types';

const CONTAINER_ID = 'sort-button-container-id';

interface Props<T extends string = string> {
  value: SortInput<T>;
  onChange: (value: SortInput<T>) => void;
  className?: string;
  children: ReactNode;
}

const SortButtonRoot = <T extends string = string>({
  value,
  onChange,
  className,
  children,
}: Props<T>) => {
  const handleDirectionChange = useCallback(() => {
    onChange({
      ...value,
      direction: value.direction === 'asc' ? 'desc' : 'asc',
    });
  }, [onChange, value]);

  const handleKeyChange = useCallback(
    (key: T) => {
      onChange({
        ...value,
        key,
      });
    },
    [onChange, value],
  );

  return (
    <div
      id={CONTAINER_ID}
      className={cn(
        'flex items-center justify-between border border-input rounded-sm h-12 min-w-48',
        className,
      )}
    >
      <Select value={value.key} onValueChange={handleKeyChange}>
        <div className="w-full flex items-center overflow-hidden">
          <SelectTrigger
            className="border-none focus-visible:ring-0 opacity-100 [&>svg]:opacity-100 [&>svg]:!text-foreground"
            aria-label={`sort by ${value.key}, ${value.direction} order`}
            id="sort-button"
          >
            <div className="w-full flex items-center overflow-hidden [&>span]:truncate">
              <SelectValue />
            </div>
          </SelectTrigger>
        </div>

        <div className="flex items-center">
          <div className="border-l border-solid border-input h-8 bg-input mr-1" />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="toggle sort direction"
            id="toggle-sort-direction-button"
            className="flex items-center gap-2 mr-1 h-8 w-8"
            onClick={handleDirectionChange}
          >
            <SortDirectionIndicator direction={value.direction} />
          </Button>
        </div>
        {children}
      </Select>
    </div>
  );
};

const SortButtonOptions = ({ children }: { children: ReactNode }) => {
  const { width } = useContainerWidth(CONTAINER_ID);
  return <SelectContent style={{ width }}>{children}</SelectContent>;
};

export const SortButton = Object.assign(SortButtonRoot, {
  Options: SortButtonOptions,
  Group: SelectGroup,
  GroupLabel: SelectLabel,
  Item: SelectItem,
  Separator: SelectSeparator,
});
